/**
 * Nosto-QuickBuyTracking.js
 *
 * Tracking global de Add to Cart realizados desde el Quick Buy de Nosto.
 *
 * Responsabilidades:
 * - escuchar "nosto:quick-buy:add";
 * - dirigir PLP hacia NostoPlpController;
 * - dirigir Search/Autocomplete hacia NostoSearchController;
 * - registrar attribution de Recommendations usando resultId.
 *
 * Este archivo NO agrega productos al carrito.
 * Shopify/Broadcast continúa siendo responsable del Add to Cart.
 */

(function initializeNostoQuickBuyTracking(global) {
    "use strict";

    /**
     * Evita registrar el listener más de una vez.
     */
    if (
        global
            .NOSTO_QUICK_BUY_ADD_TRACKING_BOUND
    ) {
        return;
    }
    if (global.NostoQuickBuyModal) {
        return;
    }

    /**
     * Convierte cualquier valor a string limpio.
     */
    function normalizeString(value) {
        return String(
            value ?? ""
        ).trim();
    }

    /**
     * Obtiene el tipo de tracking que originó el Quick Buy.
     */
    function getTrack(detail) {
        return normalizeString(
            detail?.track ||
            detail?.context?.track ||
            ""
        );
    }

    /**
     * Obtiene el resultId utilizado para attribution
     * de recomendaciones.
     */
    function getAttributionKey(detail) {
        return normalizeString(
            detail?.attributionKey ||
            detail?.context?.attributionKey ||
            detail?.context?.resultId ||
            ""
        ).replace(
            /^\?nosto=/,
            ""
        );
    }

    /**
     * Tracking de Add to Cart proveniente del PLP.
     */
    async function trackCategoryAdd(
        detail
    ) {
        const controller =
            global.NOSTO_PLP
                ?.controller;

        if (!controller) {
            console.warn(
                "[Nosto Tracking] No se encontró el controller de PLP.",
                detail
            );

            return false;
        }

        return controller
            .recordProductAddToCart(
                detail.productId,
                {
                    fallbackHit:
                        detail.hit,
                }
            );
    }

    /**
     * Tracking de Add to Cart proveniente de
     * Search o Autocomplete.
     */
    async function trackSearchAdd(
        detail,
        track
    ) {
        const controller =
            global.NOSTO_SEARCH
                ?.controller;

        if (!controller) {
            console.warn(
                "[Nosto Tracking] No se encontró el controller de Search.",
                detail
            );

            return false;
        }

        const context =
            track === "autocomplete"
                ? "modal"
                : "page";

        return controller
            .recordProductAddToCart(
                detail.productId,
                context,
                {
                    fallbackHit:
                        detail.hit,
                }
            );
    }

    /**
     * Tracking de Add to Cart proveniente
     * de una recomendación de Nosto.
     *
     * Aquí no modificamos el carrito.
     * Únicamente notificamos la attribution a Nosto.
     */
    async function trackRecommendationAdd(
        detail
    ) {
        const productId =
            normalizeString(
                detail.productId
            );

        const resultId =
            getAttributionKey(
                detail
            );

        if (
            !productId ||
            !resultId
        ) {
            console.warn(
                "[Nosto Tracking] Faltan datos para attribution de recommendation.",
                {
                    productId,
                    resultId,
                    detail,
                }
            );

            return false;
        }

        if (
            typeof global.nostojs !==
            "function"
        ) {
            console.warn(
                "[Nosto Tracking] nostojs no está disponible.",
                {
                    productId,
                    resultId,
                }
            );

            return false;
        }

        await new Promise(
            (resolve, reject) => {
                try {
                    global.nostojs((api) => {
                        try {
                            api
                                .recordAttribution({
                                    type: "cp",

                                    target:
                                        String(
                                            productId
                                        ),

                                    ref:
                                        String(
                                            resultId
                                        ),

                                    refType:
                                        "api",
                                })
                                .done();

                            resolve();
                        } catch (error) {
                            reject(error);
                        }
                    });
                } catch (error) {
                    reject(error);
                }
            }
        );

        return true;
    }

    /**
     * Dirige el evento al tracker correspondiente.
     */
    async function handleQuickBuyAdd(
        event
    ) {
        const detail =
            event.detail || {};

        const track =
            getTrack(
                detail
            );

        console.log(
            "[Nosto Tracking] Evento recibido",
            {
                track,

                productId:
                    detail.productId,

                attributionKey:
                    getAttributionKey(
                        detail
                    ),

                hit:
                    detail.hit,

                context:
                    detail.context,

                hasPlpController:
                    Boolean(
                        global.NOSTO_PLP
                            ?.controller
                    ),

                hasSearchController:
                    Boolean(
                        global.NOSTO_SEARCH
                            ?.controller
                    ),
            }
        );

        if (!track) {
            console.warn(
                "[Nosto Tracking] El evento no contiene track.",
                detail
            );

            return;
        }

        try {
            let success = false;

            switch (track) {
                case "category":
                    success =
                        await trackCategoryAdd(
                            detail
                        );
                    break;

                case "serp":
                case "autocomplete":
                    success =
                        await trackSearchAdd(
                            detail,
                            track
                        );
                    break;

                case "recommendation":
                    success =
                        await trackRecommendationAdd(
                            detail
                        );
                    break;

                default:
                    console.warn(
                        "[Nosto Tracking] Track no reconocido.",
                        {
                            track,
                            detail,
                        }
                    );

                    return;
            }

            console.log(
                "[Nosto Tracking] Resultado",
                {
                    track,

                    productId:
                        detail.productId,

                    attributionKey:
                        getAttributionKey(
                            detail
                        ),

                    success,
                }
            );

            if (!success) {
                console.warn(
                    "[Nosto Tracking] No fue posible registrar el Add to Cart.",
                    {
                        track,

                        productId:
                            detail.productId,

                        hit:
                            detail.hit,

                        context:
                            detail.context,
                    }
                );
            }
        } catch (error) {
            console.error(
                "[Nosto Tracking] Ocurrió un error al registrar el Add to Cart.",
                {
                    track,

                    productId:
                        detail.productId,

                    error,
                }
            );
        }
    }

    document.addEventListener(
        "nosto:quick-buy:add",
        handleQuickBuyAdd
    );

    global
        .NOSTO_QUICK_BUY_ADD_TRACKING_BOUND =
        true;

    global
        .NOSTO_QUICK_BUY_ADD_TRACKING_HANDLER =
        handleQuickBuyAdd;

})(window);